<template>
    <div class="selection:bg-brand-500 selection:text-white pb-24 overflow-x-hidden">
        <!-- Hero / Header -->
        <header class="relative pt-20 pb-24 sm:pt-24 sm:pb-20 border-b border-app">
            <div
                class="absolute inset-0 -z-10 bg-[radial-gradient(45%_45%_at_50%_40%,rgba(var(--core-brand-500),0.06)_0%,transparent_100%)]">
            </div>
            <!-- Floating blurred blobs -->
            <div
                class="absolute -top-10 left-[10%] -z-10 h-56 w-56 rounded-full bg-brand-500/10 blur-3xl animate-float-slow">
            </div>
            <div
                class="absolute top-20 right-[8%] -z-10 h-72 w-72 rounded-full bg-primary-500/10 blur-3xl animate-float-slower">
            </div>

            <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div class="text-center">
                    <div
                        class="inline-flex items-center gap-2 rounded-full border border-brand-500/20 bg-brand-500/5 backdrop-blur-md px-4 py-1.5 text-xs font-bold text-brand-600 mb-6 uppercase tracking-wider shadow-sm">
                        <Calendar class="h-4 w-4" /> What's Coming Up
                    </div>
                    <h1
                        class="mx-auto max-w-4xl text-5xl font-extrabold tracking-tight text-header sm:text-7xl leading-[1.1]">
                        Events worth
                        <span
                            class="bg-gradient-to-r to-brand-500 via-primary-500 from-secondary-500 bg-clip-text text-transparent font-extrabold">showing
                            up</span> for
                    </h1>
                    <p class="mt-8 text-xl text-body/80 leading-relaxed max-w-2xl mx-auto">
                        Workshops, meetups and gatherings curated for the community. Reserve your spot before seats run
                        out.
                    </p>
                </div>
            </div>
        </header>

        <!-- Loading State -->
        <div v-if="loading" class="flex flex-col items-center justify-center py-40">
            <CoreLoader/>
            <p class="text-body/80 font-bold text-sm tracking-widest uppercase">Loading events...</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="events.length === 0" class="mx-auto max-w-3xl px-4 py-32 text-center">
            <div class="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-card border border-app">
                <Calendar class="h-8 w-8 text-body/80" />
            </div>
            <h3 class="text-2xl font-bold text-header">No events scheduled yet</h3>
            <p class="text-body/80 mt-2">Check back soon — new events are added regularly.</p>
        </div>

        <!-- Overlapping Event List -->
        <main v-else ref="listRef" class="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24 space-y-20 lg:space-y-28">
            <EventCard v-for="(event, i) in events" :key="event.id" :item="event" :index="i" />
        </main>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { Calendar } from 'lucide-vue-next';
import { fetchEvents, fetchUpdates } from '~/utils/api';
import { EventItem } from '~/types';
import EventCard from '~/components/events/EventCard.vue';
import { sampleEvents } from '~/components/events/data';
import CoreLoader from '@core/components/CoreLoader.vue';

definePageMeta({ name: 'ListOfEvents' });

// Fetched during the server render; client-side navigation shows the loader until it arrives.
const { data, status } = useAsyncData('events', async (): Promise<EventItem[]> => {
    try {
        const list: EventItem[] = (await fetchEvents()) || [];
        return list.length ? list : sampleEvents;
    } catch (e) {
        console.error('Failed to fetch events', e);
        return sampleEvents;
    }
}, { lazy: true });
const events = computed<EventItem[]>(() => data.value || sampleEvents);
const loading = computed(() => status.value === 'pending');
const listRef = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;
 
const setupObserver = async () => {
    await nextTick();
    if (!listRef.value) return;
    observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('revealed');
                    observer?.unobserve(entry.target);
                }
            });
        },
        { threshold: 0.15 }
    );
    listRef.value.querySelectorAll('[data-reveal]').forEach((el) => observer?.observe(el));
};

// Reveal-on-scroll starts once the cards are in the DOM (after hydration, or when a
// client-side fetch finishes).
onMounted(() => {
    if (!loading.value) setupObserver();
});
watch(loading, (isLoading) => {
    if (!isLoading) setupObserver();
});
onBeforeUnmount(() => observer?.disconnect());
</script>

<style scoped>
.bg-card {
    background-color: rgb(var(--core-card-bg));
}

.border-app {
    border-color: rgb(var(--core-app-border));
}

.text-header {
    color: rgb(var(--core-header-text));
}

.text-muted {
    color: rgb(var(--core-muted-text));
}

/* Floating blobs */
@keyframes float-slow {

    0%,
    100% {
        transform: translateY(0) translateX(0);
    }

    50% {
        transform: translateY(-24px) translateX(12px);
    }
}

@keyframes float-slower {

    0%,
    100% {
        transform: translateY(0) translateX(0);
    }

    50% {
        transform: translateY(20px) translateX(-16px);
    }
}

.animate-float-slow {
    animation: float-slow 9s ease-in-out infinite;
}

.animate-float-slower {
    animation: float-slower 13s ease-in-out infinite;
}
</style>
